import { Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { InputComponent } from '..';
import { INPUT_TYPE, LABEL_TYPE } from '../types';
import { ICheckboxPropsInCheckboxGroup, ICommonInputProps } from './types';

export interface ICheckboxGroup extends ICommonInputProps {
  checkboxGroupProps?: ICheckboxPropsInCheckboxGroup[];
}

const CheckboxGroup: React.FC<ICheckboxGroup> = ({ checkboxGroupProps = [], onChange, disabled, inputSettings }) => {
  const [selectedData, setSelectedData] = useState<Record<string, boolean>>({});
  const allCheckedRef = useRef<Record<string, boolean>>({});
  const allUnCheckedRef = useRef<Record<string, boolean>>({});
  const selectedDataAsArray = useMemo(() => Object.values(selectedData), [selectedData]);
  const isAllCheckboxCurrentlyChecked = useMemo(
    () => selectedDataAsArray.length > 0 && selectedDataAsArray.every((data) => data),
    [selectedDataAsArray],
  );

  useEffect(() => {
    onChange?.(selectedData);
  }, [selectedData]);

  useEffect(() => {
    if (!checkboxGroupProps || !checkboxGroupProps.length) return;
    const checkboxStates = checkboxGroupProps.reduce((acc, prop) => {
      // set allCheckedRef and allUnCheckedRef
      if (!allCheckedRef.current[prop.name]) allCheckedRef.current[prop.name] = true;
      if (!allUnCheckedRef.current[prop.name]) allUnCheckedRef.current[prop.name] = false;
      // =====================================
      if (selectedData && selectedData[prop.name] !== undefined) return acc;
      acc[prop.name] = prop.value || false;
      return acc;
    }, {} as Record<string, boolean>);
    if (!isEmpty(checkboxStates)) {
      setSelectedData(checkboxStates);
    }
  }, [checkboxGroupProps]);

  const onAllCheckboxClick = () => {
    const newCheckboxState = isAllCheckboxCurrentlyChecked ? allUnCheckedRef.current : allCheckedRef.current;
    // onChange?.(newCheckboxState);
    setSelectedData(newCheckboxState);
  };

  const onSingleCheckboxClick = (name: string) => {
    const newCheckboxState = { ...selectedData, [name]: !selectedData[name] };
    // onChange?.(newCheckboxState);
    setSelectedData(newCheckboxState);
  };

  const checkType = inputSettings?.useSwitch ? INPUT_TYPE.SWITCH : INPUT_TYPE.CHECKBOX;

  const generateCheckboxes = (singleCheckboxProps: ICheckboxPropsInCheckboxGroup) => (
    <Col
      className='gx-px-0'
      span={24}
      xs={singleCheckboxProps.size?.xs}
      sm={singleCheckboxProps.size?.sm}
      md={singleCheckboxProps.size?.md}
      lg={singleCheckboxProps.size?.lg}
      key={singleCheckboxProps.name}>
      <InputComponent
        type={checkType}
        name={singleCheckboxProps.name}
        label={singleCheckboxProps.label}
        labelType={LABEL_TYPE.right}
        onChange={() => onSingleCheckboxClick(singleCheckboxProps.name)}
        value={selectedData[singleCheckboxProps.name] || false}
        disabled={singleCheckboxProps.disabled}
      />
    </Col>
  );

  return (
    <>
      {checkboxGroupProps.length === 0 && inputSettings?.noDataRender && (
        <Row className='gx-m-0 gx-w-100'>
          <Col className='gx-px-0' span={24}>
            {inputSettings?.noDataRender}
          </Col>
        </Row>
      )}
      {checkboxGroupProps.length > 0 && (
        <Row className='gx-m-0 gx-w-100'>
          {checkboxGroupProps.length > 1 && (
            <Col className='gx-px-0 gx-mt-2' span={24}>
              <InputComponent
                type={checkType}
                name='all'
                label='All'
                labelType={LABEL_TYPE.right}
                onChange={onAllCheckboxClick}
                value={isAllCheckboxCurrentlyChecked}
                disabled={disabled}
              />
            </Col>
          )}
          {checkboxGroupProps.map(generateCheckboxes)}
        </Row>
      )}
    </>
  );
};

export default CheckboxGroup;
